import React, { useState } from 'react';
import { createUserWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { doc, setDoc, getDocs, query, collection, where, writeBatch } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useFirebase } from '../App'; // Adjust path if App.js is in a parent folder

export const RegisterNowPage = ({ navigate }) => {
    // Access global firebase instances from Context
    const { auth, db, storage, saveUserThemePreference } = useFirebase();

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        userType: 'individual_delegate', // Default
        groupCode: '',
        teacher: {
            school: '',
            city: '',
            country: '',
            phone: '',
        },
    });

    const [cvFile, setCvFile] = useState(null);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // --- Helper: Generate Unique 6-Char Code ---
    const generateUniqueCode = () => {
        return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('teacher.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                teacher: { ...prev.teacher, [field]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setMessage('File size exceeds 10MB limit.');
                setIsError(true);
                setCvFile(null);
                return;
            }
            setMessage('');
            setIsError(false);
            setCvFile(file);
        }
    };

    // --- Main Submission Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setIsError(false);
        setLoading(true);

        // 1. Basic Validations
        if (formData.password !== confirmPassword) {
            setMessage('Passwords do not match.');
            setIsError(true);
            setLoading(false);
            return;
        }

        if (formData.userType === 'staff/chair' && !cvFile) {
            setMessage('Please upload your CV.');
            setIsError(true);
            setLoading(false);
            return;
        }

        let userCredential;

        try {
            // 2. Create Auth User (Required first to pass Firestore Rules)
            userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            const user = userCredential.user;
            const userUid = user.uid;

            // 3. Prepare Batch Write
            const batch = writeBatch(db);
            
            // Base User Data
            let userData = {
                uid: userUid,
                email: formData.email,
                fullName: formData.fullName,
                userType: formData.userType,
                createdAt: new Date().toISOString(),
                // Initialize default fields to avoid "undefined" errors later
                registrationStatus: 'Pending', 
            };

            // 4. Role Specific Logic
            if (formData.userType === 'group_delegate') {
                // QUERY TEACHER CODE
                // Note: We can query this now because we are authenticated (step 2)
                const teacherCodesRef = collection(db, 'teacherCodes');
                const q = query(teacherCodesRef, where('teacherCode', '==', formData.groupCode.trim()));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    throw new Error("Invalid Group Code. Please check with your teacher.");
                }

                const teacherData = querySnapshot.docs[0].data();
                
                // Add Delegate Info to Batch
                const delegateInfoRef = doc(db, `users/${userUid}/thessmunDelegateInfo`, 'myDelegateInfo');
                batch.set(delegateInfoRef, {
                    uid: userUid,
                    email: formData.email,
                    fullName: formData.fullName,
                    isGroupDelegate: true,
                    groupCode: formData.groupCode,
                    teacherUid: teacherData.teacherUid, // Link to teacher
                    registrationStatus: 'Pending',
                    committeeAllocation: 'Pending',
                    countryAllocation: 'Pending',
                    isApplicationComplete: false,
                    registeredAt: new Date().toISOString(),
                });

                userData.isGroupDelegate = true;
                userData.groupCode = formData.groupCode;
            } 
            else if (formData.userType === 'individual_delegate') {
                // Add Delegate Info to Batch
                const delegateInfoRef = doc(db, `users/${userUid}/thessmunDelegateInfo`, 'myDelegateInfo');
                batch.set(delegateInfoRef, {
                    uid: userUid,
                    email: formData.email,
                    fullName: formData.fullName,
                    isGroupDelegate: false,
                    registrationStatus: 'Pending',
                    committeeAllocation: 'Pending',
                    countryAllocation: 'Pending',
                    isApplicationComplete: false,
                    registeredAt: new Date().toISOString(),
                });
            }
            else if (formData.userType === 'teacher') {
                const newTeacherCode = generateUniqueCode();
                
                userData = {
                    ...userData,
                    teacherCode: newTeacherCode,
                    school: formData.teacher.school,
                    city: formData.teacher.city,
                    country: formData.teacher.country,
                    phone: formData.teacher.phone,
                };

                // Create Public Teacher Code Mapping (For delegates to find later)
                const publicCodeRef = doc(db, 'teacherCodes', userUid);
                batch.set(publicCodeRef, {
                    teacherUid: userUid,
                    teacherCode: newTeacherCode,
                    school: formData.teacher.school // Optional: helps debug
                });
            }
            else if (formData.userType === 'staff/chair') {
                // Upload CV
                setMessage('Uploading CV...');
                const storageRef = ref(storage, `staff_cvs/${userUid}/${cvFile.name}`);
                const uploadTask = uploadBytesResumable(storageRef, cvFile);

                // Wait for upload
                await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(progress);
                        },
                        (error) => reject(error),
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            userData.cvUrl = downloadURL;
                            resolve();
                        }
                    );
                });
            }

            // 5. Commit Batch (Write User Profile)
            const userDocRef = doc(db, 'users', userUid);
            batch.set(userDocRef, userData);

            await batch.commit();

            // 6. Finalize
            saveUserThemePreference(true); // Enable Dark Mode
            setMessage('Registration Successful! Redirecting...');
            setTimeout(() => navigate('home'), 1500);

        } catch (error) {
            console.error("Registration Error:", error);
            setLoading(false);
            setIsError(true);
            
            // CLEANUP: If we created an Auth user but failed database logic (e.g. invalid code),
            // we must delete the auth user so they can try again with the same email.
            if (userCredential && userCredential.user) {
                await deleteUser(userCredential.user).catch(e => console.error("Cleanup failed", e));
            }

            if (error.message.includes('Invalid Group Code')) {
                setMessage(error.message);
            } else if (error.code === 'auth/email-already-in-use') {
                setMessage("This email is already in use.");
            } else if (error.code === 'auth/weak-password') {
                setMessage("Password should be at least 6 characters.");
            } else {
                setMessage("Registration failed. Please try again.");
            }
        }
    };

    return (
        <section className="container mx-auto px-6 py-16 animate-fade-in" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-body-text)' }}>
            <h2 className="text-4xl font-bold text-center mb-8">Register Now</h2>
            
            <div className="p-8 rounded-lg shadow-xl max-w-2xl mx-auto border" 
                 style={{ backgroundColor: 'var(--color-section1-card-bg)', borderColor: 'var(--color-section1-card-border)' }}>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Role Selector */}
                    <div>
                        <label className="block text-sm font-medium mb-2">I am registering as:</label>
                        <select
                            name="userType"
                            value={formData.userType}
                            onChange={(e) => setFormData(prev => ({...prev, userType: e.target.value}))}
                            className="w-full px-4 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 text-black"
                        >
                            <option value="individual_delegate">Individual Delegate</option>
                            <option value="group_delegate">Delegate (Part of a Group)</option>
                            <option value="teacher">Teacher / Advisor</option>
                            <option value="staff/chair">Staff / Chair</option>
                        </select>
                    </div>

                    {/* Common Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">Full Name</label>
                            <input type="text" name="fullName" required 
                                value={formData.fullName} onChange={handleChange}
                                className="w-full px-4 py-2 border rounded-md text-black" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Email</label>
                            <input type="email" name="email" required 
                                value={formData.email} onChange={handleChange}
                                className="w-full px-4 py-2 border rounded-md text-black" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">Password</label>
                            <input type="password" name="password" required 
                                value={formData.password} onChange={handleChange}
                                className="w-full px-4 py-2 border rounded-md text-black" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Confirm Password</label>
                            <input type="password" value={confirmPassword} required 
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2 border rounded-md text-black" />
                        </div>
                    </div>

                    {/* Conditional: Group Delegate */}
                    {formData.userType === 'group_delegate' && (
                        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                            <label className="block text-sm font-bold text-blue-800">Group Code</label>
                            <p className="text-xs text-blue-600 mb-2">Ask your teacher/advisor for this code.</p>
                            <input type="text" name="groupCode" required 
                                value={formData.groupCode} onChange={handleChange}
                                placeholder="e.g. X9Y2Z1"
                                className="w-full px-4 py-2 border rounded-md text-black uppercase" />
                        </div>
                    )}

                    {/* Conditional: Teacher */}
                    {formData.userType === 'teacher' && (
                        <div className="space-y-4 border-t pt-4">
                            <h4 className="font-bold text-lg">School Information</h4>
                            <div>
                                <label className="block text-sm font-medium">School Name</label>
                                <input type="text" name="teacher.school" required 
                                    value={formData.teacher.school} onChange={handleChange}
                                    className="w-full px-4 py-2 border rounded-md text-black" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium">City</label>
                                    <input type="text" name="teacher.city" required 
                                        value={formData.teacher.city} onChange={handleChange}
                                        className="w-full px-4 py-2 border rounded-md text-black" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">Country</label>
                                    <input type="text" name="teacher.country" required 
                                        value={formData.teacher.country} onChange={handleChange}
                                        className="w-full px-4 py-2 border rounded-md text-black" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Phone Number</label>
                                <input type="tel" name="teacher.phone" required 
                                    value={formData.teacher.phone} onChange={handleChange}
                                    className="w-full px-4 py-2 border rounded-md text-black" />
                            </div>
                        </div>
                    )}

                    {/* Conditional: Staff */}
                    {formData.userType === 'staff/chair' && (
                        <div className="border-t pt-4">
                             <label className="block text-sm font-medium mb-2">Upload CV (PDF/Word, Max 10MB)</label>
                             <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} required
                                    className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                             
                             {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                             )}
                        </div>
                    )}

                    <button type="submit" disabled={loading}
                        className={`w-full py-3 px-4 rounded-md font-bold text-white transition-colors shadow-lg
                        ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {loading ? 'Processing...' : 'Create Account'}
                    </button>
                </form>

                {message && (
                    <div className={`mt-4 p-4 rounded-md text-center font-medium
                        ${isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {message}
                    </div>
                )}
                
                <p className="mt-6 text-center text-sm">
                    Already have an account?{' '}
                    <button onClick={() => navigate('login')} className="text-blue-500 hover:underline font-bold">
                        Log In
                    </button>
                </p>
            </div>
        </section>
    );
};